import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiServices from "../../services/apiServices";

// Fetch customers - Async Thunk with pagination and filters
export const fetchCustomers = createAsyncThunk(
  "customers/fetchCustomers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const { page = 1, limit = 10, search = "", sort = {} } = params;
      let url = `customers/with-accounts?page=${page}&limit=${limit}`;

      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (sort.key) {
        url += `&sortBy=${sort.key}&sortOrder=${sort.direction}`;
      }

      const response = await apiServices.get(url);
      return response.data; // Now returns { data, total, page, limit, totalPages }
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching customers");
    }
  }
);

// Add a new customer
export const addCustomer = createAsyncThunk(
  "customers/addCustomer",
  async (customerData, { rejectWithValue }) => {
    try {
      const response = await apiServices.post("customers", customerData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error adding customer");
    }
  }
);

// Delete a customer
export const deleteCustomer = createAsyncThunk(
  "customers/deleteCustomer",
  async (customerId, { rejectWithValue }) => {
    try {
      await apiServices.delete(`customers/${customerId}`);
      return customerId;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error deleting customer");
    }
  }
);

// Update a customer
export const updateCustomer = createAsyncThunk(
  "customers/updateCustomer",
  async (updatedData, { rejectWithValue }) => {
    try {
      const response = await apiServices.put(`customers/${updatedData._id}`, updatedData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error updating customer");
    }
  }
);

export const getCustomerById = createAsyncThunk(
  "customers/getCustomerById",
  async (customerId, { rejectWithValue }) => {
    try {
      const response = await apiServices.get(`customers/${customerId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching customer details");
    }
  }
);

// Slice
const customerSlice = createSlice({
  name: "customers",
  initialState: {
    customers: [],
    searchResults: [], // Isolated array for dropdown searches
    total: 0,
    currentPage: 1,
    totalPages: 1,
    customer: null,
    loading: false,
    searchLoading: false, // Isolated loading flag for dropdown searches only — does NOT trigger POSPortal overlay
    error: null,
  },
  reducers: {
    resetCustomerState: (state) => {
      state.error = null;
      state.loading = false;
    },
    clearSearchResults: (state) => {
      // Wipe stale dropdown results when user clears the search box
      state.searchResults = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Customer By ID
      .addCase(getCustomerById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCustomerById.fulfilled, (state, action) => {
        state.loading = false;
        state.customer = action.payload;
      })
      .addCase(getCustomerById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Customers (Paginated)
      .addCase(fetchCustomers.pending, (state, action) => {
        const isDropdown = !!action.meta.arg?.isDropdownSearch;
        if (isDropdown) {
          // Dropdown/search calls use the isolated flag — does NOT trigger the POS full-screen overlay
          state.searchLoading = true;
        } else {
          // Full list fetches (initial load, pagination) use the global flag
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.searchLoading = false;
        const isDropdownSearch = !!action.meta.arg?.isDropdownSearch;
        const results = action.payload.data || action.payload;

        if (isDropdownSearch) {
          // Update only the temporary search array for dropdowns
          state.searchResults = Array.isArray(results) ? results : [];
        } else {
          // Update the main dataset for list views/lookups
          if (action.payload.data && Array.isArray(action.payload.data)) {
            state.customers = action.payload.data;
            state.total = action.payload.total || action.payload.data.length;
            state.currentPage = action.payload.page || 1;
            state.totalPages = action.payload.totalPages || 1;
          } else if (Array.isArray(action.payload)) {
            state.customers = action.payload;
            state.total = action.payload.length;
          }
        }
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.loading = false;
        state.searchLoading = false;
        state.error = action.payload;
      })
      // Add Customer
      .addCase(addCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addCustomer.fulfilled, (state, action) => {
        state.loading = false;
        // In server-side pagination, we usually re-fetch, but for simple UX:
        if (Array.isArray(state.customers)) {
          state.customers.unshift(action.payload.customer || action.payload);
        }
      })
      .addCase(addCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Customer
      .addCase(deleteCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.customers = state.customers.filter((c) => c._id !== action.payload);
      })
      .addCase(deleteCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Customer
      .addCase(updateCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.customer || action.payload;
        const index = state.customers.findIndex((c) => c._id === updated._id);
        if (index !== -1) {
          state.customers[index] = updated;
        }
      })
      .addCase(updateCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetCustomerState, clearSearchResults } = customerSlice.actions;
export default customerSlice.reducer;
